import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { AUTOSAVE_SAVED } from '@/types/flow.type';
import { DataState, INITIAL_DATA_STATE } from '@/types/data.type';
import { DESC_SORT_DIRECTION, SortDirection } from '@/types/sort.type';

import {
    FlowBuilderLibraryPage,
    FlowBuilderSliceState,
} from './flowBuilder.type';

const flowBuilderInitialState: FlowBuilderSliceState = {
    autosaveStatus: AUTOSAVE_SAVED,
    library: {
        codes: [],
        page: 0,
        totalPages: 0,
        query: null,
        dataState: INITIAL_DATA_STATE,
        search: '',
        direction: DESC_SORT_DIRECTION,
    },
};

export const FLOW_BUILDER_REDUCER_NAME = 'flowBuilder';

export const flowBuilderSlice = createSlice({
    name: FLOW_BUILDER_REDUCER_NAME,
    initialState: flowBuilderInitialState,
    reducers: {
        setAutosaveStatus: (
            state,
            action: PayloadAction<FlowBuilderSliceState['autosaveStatus']>,
        ) => {
            state.autosaveStatus = action.payload;
        },
        setLibraryPage: (
            state,
            action: PayloadAction<FlowBuilderLibraryPage>,
        ) => {
            const { codes, page, totalPages, query } = action.payload;

            state.library.codes = codes;
            state.library.page = page;
            state.library.totalPages = totalPages;
            state.library.query = query;
        },
        appendLibraryPage: (
            state,
            action: PayloadAction<FlowBuilderLibraryPage>,
        ) => {
            const { codes, page, totalPages, query } = action.payload;

            state.library.codes = [
                ...state.library.codes,
                // NOTE: Reels can shift pages when new ones are added
                ...codes.filter((code) => !state.library.codes.includes(code)),
            ];
            state.library.page = page;
            state.library.totalPages = totalPages;
            state.library.query = query;
        },
        setLibraryDataState: (state, action: PayloadAction<DataState>) => {
            state.library.dataState = action.payload;
        },
        setLibrarySearch: (state, action: PayloadAction<string>) => {
            state.library.search = action.payload;
        },
        setLibraryDirection: (state, action: PayloadAction<SortDirection>) => {
            state.library.direction = action.payload;
        },
        resetLibraryFilters: (state) => {
            const { search, direction } = flowBuilderInitialState.library;
            const { query } = state.library;

            state.library.search = search;
            state.library.direction = direction;

            // NOTE: Filtered reels shouldn't show up when the flyover is reopened
            if (query?.search !== search || query?.direction !== direction) {
                state.library.codes = [];
                state.library.page = 0;
                state.library.totalPages = 0;
                state.library.query = null;
            }
        },
        resetLibrary: (state) => {
            state.library = flowBuilderInitialState.library;
        },
    },
});

export const {
    setAutosaveStatus,
    setLibraryPage,
    appendLibraryPage,
    setLibraryDataState,
    setLibrarySearch,
    setLibraryDirection,
    resetLibraryFilters,
    resetLibrary,
} = flowBuilderSlice.actions;

export const flowBuilderReducer = flowBuilderSlice.reducer;
